import { useState, useEffect, Fragment } from 'react'
import { useAdmin } from '../context/AdminContext'
import {
  createAdminApi,
  CharacterDetail,
  CharacterAnimConfig,
  GestureDTO,
  GesturePoolEntrySummary,
} from '../services/adminApi'
import GesturePoolStats from '../components/GesturePoolStats'
import styles from './CharactersPage.module.css'

interface Props {
  characterId: string
}

// gesturePoolGroup is one gesture key plus its variants, carrying the per-key
// aggregates (variant count, total plays) that the stats panel's separate "top
// keys" table used to duplicate. Folding them into the entries table is what
// lets that table be the ONE place a pool is read and curated — and unlike the
// old stats table, which was capped at the server's top 10, this covers every
// key the character has.
interface gesturePoolGroup {
  gestureKey: string
  entries: GesturePoolEntrySummary[]
  plays: number
  // fullQuality is the count that actually rotates: the background-generated,
  // pose-neutral variants. live counts the synchronous cold-miss rows, which
  // are retained as the cold-pool fallback but benched at serve time once
  // topups exist — so they are reported alongside the target, not inside it.
  // Mirrors gesturegen.fullQualityPreferred (source !== 'live').
  fullQuality: number
  live: number
}

// groupPoolEntries folds the flat entries list into one group per gesture key.
// The API already returns rows ordered by (gesture_key, created_at), but this
// groups by lookup rather than by adjacency so the table stays correct
// regardless of the order the rows arrive in.
function groupPoolEntries(entries: GesturePoolEntrySummary[]): gesturePoolGroup[] {
  const byKey = new Map<string, gesturePoolGroup>()
  for (const e of entries) {
    let g = byKey.get(e.gestureKey)
    if (!g) {
      g = { gestureKey: e.gestureKey, entries: [], plays: 0, fullQuality: 0, live: 0 }
      byKey.set(e.gestureKey, g)
    }
    g.entries.push(e)
    g.plays += e.playCount
    if (e.source !== 'live') {
      g.fullQuality++
    } else {
      g.live++
    }
  }
  return [...byKey.values()]
}


// ── Component ─────────────────────────────────────────────────────────────────

export default function CharacterDetailPage({ characterId }: Props) {
  const { apiKey } = useAdmin()

  const [character, setCharacter] = useState<CharacterDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Dynamic motion
  const [dmSaving, setDmSaving] = useState(false)
  const [dmError, setDmError] = useState('')

  // Gesture pool: toggle + entries list + delete/clear
  const [gpSaving, setGpSaving] = useState(false)
  const [gpError, setGpError] = useState('')
  const [showEntries, setShowEntries] = useState(false)
  const [entries, setEntries] = useState<GesturePoolEntrySummary[] | null>(null)
  const [entriesLoading, setEntriesLoading] = useState(false)
  const [entriesError, setEntriesError] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [clearing, setClearing] = useState(false)
  const [statsRefresh, setStatsRefresh] = useState(0)
  // Lifted out of the stats panel (the only thing that fetches stats) so the
  // per-key header rows can render "n / target variants" without hardcoding the
  // target. 0 until stats load — the denominator is then simply omitted.
  const [poolTarget, setPoolTarget] = useState(0)

  // ── Animation config state ─────────────────────────────────────────────────

  const [animConfig, setAnimConfig] = useState<CharacterAnimConfig | null>(null)
  const [animLoading, setAnimLoading] = useState(true)
  const [animError, setAnimError] = useState('')

  // Gestures
  const [newGesture, setNewGesture] = useState('')
  const [gesturesSaving, setGesturesSaving] = useState(false)

  // Vocal cues
  const [newVocalCue, setNewVocalCue] = useState('')
  const [vocalCuesSaving, setVocalCuesSaving] = useState(false)

  // Synonyms
  const [showSynonyms, setShowSynonyms] = useState(true)
  const [synonymsSaving, setSynonymsSaving] = useState(false)
  const [newSynonymStems, setNewSynonymStems] = useState('')
  const [newSynonymGesture, setNewSynonymGesture] = useState('')

  // A2F config
  const [showA2F, setShowA2F] = useState(false)
  const [a2fConfigText, setA2fConfigText] = useState('')
  const [a2fSaving, setA2fSaving] = useState(false)
  const [a2fError, setA2fError] = useState('')

  // ── Load data ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const api = createAdminApi()
    setLoading(true)
    setError('')
    api.getCharacter(characterId)
      .then(setCharacter)
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey, characterId])

  useEffect(() => {
    const api = createAdminApi()
    setAnimLoading(true)
    setAnimError('')
    api.getCharacterAnimConfig(characterId)
      .then(cfg => {
        setAnimConfig(cfg)
        setA2fConfigText(JSON.stringify(cfg.a2fConfig, null, 2))
      })
      .catch(e => setAnimError(e instanceof Error ? e.message : String(e)))
      .finally(() => setAnimLoading(false))
  }, [apiKey, characterId])

  // ── Personality edit ───────────────────────────────────────────────────────

  function startEdit() {
    setDraft(character?.personality ?? '')
    setSaveError('')
    setEditing(true)
  }

  function cancelEdit() {
    setEditing(false)
    setSaveError('')
  }

  async function savePersonality() {
    if (!character) return
    const api = createAdminApi()
    setSaving(true)
    setSaveError('')
    try {
      const updated = await api.updateCharacterPersonality(character.id, draft)
      setCharacter(updated)
      setEditing(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  // ── Dynamic motion ─────────────────────────────────────────────────────────

  async function toggleDynamicMotion() {
    if (!character || dmSaving) return
    const api = createAdminApi()
    setDmSaving(true)
    setDmError('')
    try {
      const result = await api.setCharacterDynamicMotion(character.id, !character.dynamicMotion)
      setCharacter(prev => prev ? { ...prev, dynamicMotion: result.dynamicMotion } : prev)
    } catch (e) {
      setDmError(e instanceof Error ? e.message : String(e))
    } finally {
      setDmSaving(false)
    }
  }

  async function toggleGesturePoolEnabled() {
    if (!character || gpSaving) return
    const api = createAdminApi()
    setGpSaving(true)
    setGpError('')
    try {
      const result = await api.setCharacterGesturePoolEnabled(character.id, !character.gesturePoolEnabled)
      setCharacter(prev => (prev ? { ...prev, gesturePoolEnabled: result.gesturePoolEnabled } : prev))
    } catch (e) {
      setGpError(e instanceof Error ? e.message : String(e))
    } finally {
      setGpSaving(false)
    }
  }

  async function toggleEntries() {
    const next = !showEntries
    setShowEntries(next)
    // Lazily fetch the list the first time it's expanded (or after it was cleared).
    if (next && entries === null && character) {
      setEntriesLoading(true)
      setEntriesError('')
      try {
        const rows = await createAdminApi().getCharacterGesturePool(character.id)
        setEntries(rows)
      } catch (e) {
        setEntriesError(e instanceof Error ? e.message : String(e))
      } finally {
        setEntriesLoading(false)
      }
    }
  }

  async function deleteEntry(id: string) {
    if (deletingId) return
    setDeletingId(id)
    setEntriesError('')
    try {
      await createAdminApi().deleteGesturePoolEntry(id)
      setEntries(prev => (prev ? prev.filter(e => e.id !== id) : prev))
      setStatsRefresh(n => n + 1)
    } catch (e) {
      setEntriesError(e instanceof Error ? e.message : String(e))
    } finally {
      setDeletingId(null)
    }
  }

  async function clearPool() {
    if (!character || clearing) return
    const n = entries?.length ?? 0
    if (n === 0) return
    if (!window.confirm(`Delete all ${n} pool entries for ${character.name}? This permanently removes generated clips; they will be re-generated on demand.`)) {
      return
    }
    setClearing(true)
    setEntriesError('')
    try {
      await createAdminApi().clearCharacterGesturePool(character.id)
      setEntries([])
      setStatsRefresh(nn => nn + 1)
    } catch (e) {
      setEntriesError(e instanceof Error ? e.message : String(e))
    } finally {
      setClearing(false)
    }
  }

  // ── Gestures ───────────────────────────────────────────────────────────────

  async function addGesture() {
    const name = newGesture.trim()
    if (!name || !animConfig) return
    const updated = [...animConfig.gestures.map((g: GestureDTO) => g.name), name]
    setGesturesSaving(true)
    try {
      const result = await createAdminApi().updateCharacterGestures(characterId, updated)
      setAnimConfig(result)
      setNewGesture('')
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setGesturesSaving(false)
    }
  }

  async function removeGesture(name: string) {
    if (!animConfig) return
    const updated = animConfig.gestures.filter((g: GestureDTO) => g.name !== name).map((g: GestureDTO) => g.name)
    setGesturesSaving(true)
    try {
      const result = await createAdminApi().updateCharacterGestures(characterId, updated)
      setAnimConfig(result)
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setGesturesSaving(false)
    }
  }

  // ── Vocal cues ─────────────────────────────────────────────────────────────

  async function addVocalCue() {
    const name = newVocalCue.trim()
    if (!name || !animConfig) return
    const updated = [...animConfig.vocalCues, name]
    setVocalCuesSaving(true)
    try {
      const result = await createAdminApi().updateCharacterVocalCues(characterId, updated)
      setAnimConfig(result)
      setNewVocalCue('')
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setVocalCuesSaving(false)
    }
  }

  async function removeVocalCue(name: string) {
    if (!animConfig) return
    const updated = animConfig.vocalCues.filter(v => v !== name)
    setVocalCuesSaving(true)
    try {
      const result = await createAdminApi().updateCharacterVocalCues(characterId, updated)
      setAnimConfig(result)
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setVocalCuesSaving(false)
    }
  }

  // ── Synonyms ───────────────────────────────────────────────────────────────

  async function addSynonym() {
    const stems = newSynonymStems.trim()
    const gesture = newSynonymGesture.trim()
    if (!stems || !gesture || !animConfig) return
    const updated = [...animConfig.gestureSynonyms, { stemWords: stems, gestureName: gesture }]
    setSynonymsSaving(true)
    try {
      const result = await createAdminApi().updateCharacterGestureSynonyms(characterId, updated)
      setAnimConfig(result)
      setNewSynonymStems('')
      setNewSynonymGesture('')
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setSynonymsSaving(false)
    }
  }

  async function removeSynonym(idx: number) {
    if (!animConfig) return
    const updated = animConfig.gestureSynonyms.filter((_, i) => i !== idx)
    setSynonymsSaving(true)
    try {
      const result = await createAdminApi().updateCharacterGestureSynonyms(characterId, updated)
      setAnimConfig(result)
    } catch (e) {
      setAnimError(e instanceof Error ? e.message : String(e))
    } finally {
      setSynonymsSaving(false)
    }
  }

  // ── A2F config ─────────────────────────────────────────────────────────────

  async function saveA2FConfig() {
    setA2fSaving(true)
    setA2fError('')
    try {
      const config = JSON.parse(a2fConfigText)
      await createAdminApi().updateCharacterA2FConfig(characterId, config)
    } catch (e) {
      setA2fError(e instanceof Error ? e.message : String(e))
    } finally {
      setA2fSaving(false)
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString()
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={styles.detailPage}>
      <header className={styles.detailHeader}>
        <a href="#characters" className={styles.back}>← Characters</a>
        {character && (
          <>
            <h1>{character.name}</h1>
            {character.version > 1 && (
              <span className={styles.versionBadge}>v{character.version}</span>
            )}
          </>
        )}
      </header>

      <div className={styles.detailContent}>
        {loading && <p className={styles.empty}>Loading…</p>}
        {error && <p className={styles.error}>{error}</p>}

        {character && (
          <>
            {/* ── Identity ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Identity</p>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>ID</span>
                <span className={styles.fieldMono}>{character.id}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Name</span>
                <span className={styles.fieldValue}>{character.name}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Version</span>
                <span className={styles.fieldValue}>{character.version}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Created</span>
                <span className={styles.fieldValue}>{formatDate(character.createdAt)}</span>
              </div>
              <div className={styles.fieldRow}>
                <span className={styles.fieldLabel}>Updated</span>
                <span className={styles.fieldValue}>{formatDate(character.updatedAt)}</span>
              </div>
            </div>

            {/* ── Personality ── */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Personality</p>
                {!editing && (
                  <button className={styles.editBtn} onClick={startEdit}>Edit</button>
                )}
              </div>

              {editing ? (
                <>
                  <textarea
                    className={styles.personalityTextarea}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    rows={16}
                    disabled={saving}
                  />
                  {saveError && <p className={styles.saveError}>{saveError}</p>}
                  <div className={styles.editActions}>
                    <button className={styles.cancelBtn} onClick={cancelEdit} disabled={saving}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={savePersonality} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </>
              ) : (
                character.personality
                  ? <pre className={styles.personalityBlock}>{character.personality}</pre>
                  : <span className={styles.noStories}>No personality defined.</span>
              )}
            </div>

            {/* ── Motion & Gesture Pool ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Motion &amp; Gesture Pool</p>

              {/* Toggle 1: Dynamic motion (master gate) */}
              <label className={styles.toggleLabel}>
                <input
                  type="checkbox"
                  checked={character.dynamicMotion}
                  onChange={toggleDynamicMotion}
                  disabled={dmSaving}
                  aria-label="Dynamic motion"
                />
                Dynamic motion — {character.dynamicMotion ? 'Enabled' : 'Disabled'}
                {dmSaving && <span className={styles.defaultNote}>Saving…</span>}
              </label>
              {dmError && <p className={styles.saveError}>{dmError}</p>}
              <p className={styles.toggleHint}>
                Master switch for gesture generation and story motion anchors. When off,
                gesturegen never runs and motion intents are not injected into the LLM
                prompt, even if the session requests <code>gesture_gen</code>.
              </p>

              {/* Toggle 2: Gesture pool (secondary — only meaningful under dynamic motion) */}
              <label className={styles.toggleLabel} style={{ marginLeft: '1.25rem' }}>
                <input
                  type="checkbox"
                  checked={character.gesturePoolEnabled}
                  onChange={toggleGesturePoolEnabled}
                  disabled={gpSaving}
                  aria-label="Gesture pool"
                />
                Gesture pool — {character.gesturePoolEnabled ? 'Enabled' : 'Disabled'}
                {gpSaving && <span className={styles.defaultNote}>Saving…</span>}
              </label>
              {gpError && <p className={styles.saveError}>{gpError}</p>}
              <p className={styles.toggleHint} style={{ marginLeft: '1.25rem' }}>
                When off, live sessions bypass the pool: every gesture is generated fresh
                per-turn and nothing is read from or stored in the pool. Generation itself
                is still governed by Dynamic Motion
                {!character.dynamicMotion && ' — which is currently off, so generation is fully disabled'}.
              </p>
              <p className={styles.toggleHint}>
                Both settings are captured when a session starts — changes apply to new
                sessions only, not ones already in progress.
              </p>

              {/* Pool stats */}
              <GesturePoolStats
                characterId={character.id}
                refreshToken={statsRefresh}
                onTargetPoolSize={setPoolTarget}
              />

              {/* Entries list (collapsed by default) */}
              <button className={styles.synonymsToggle} onClick={toggleEntries}>
                {showEntries
                  ? 'Hide entries'
                  : `Show entries${entries ? ` (${entries.length})` : ''}`}
              </button>
              {showEntries && (
                <>
                  {entriesLoading && <span className={styles.defaultNote}>Loading…</span>}
                  {entriesError && <p className={styles.saveError}>{entriesError}</p>}
                  {entries && entries.length === 0 && !entriesLoading && (
                    <span className={styles.defaultNote}>(pool is empty — no generated variants)</span>
                  )}
                  {entries && entries.length > 0 && (
                    <div style={{ maxHeight: '22rem', overflowY: 'auto' }}>
                      <table className={styles.synonymTable}>
                        <thead>
                          <tr>
                            <th>Gesture key / variant</th>
                            <th>Class</th>
                            <th>Source</th>
                            <th>Dur</th>
                            <th>Plays</th>
                            <th>Created</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {groupPoolEntries(entries).map(g => (
                            <Fragment key={g.gestureKey}>
                              <tr className={styles.poolKeyRow}>
                                <td className={styles.poolKeyCell}>{g.gestureKey}</td>
                                <td
                                  colSpan={3}
                                  className={
                                    poolTarget > 0 && g.fullQuality < poolTarget
                                      ? styles.poolVariantsBelow
                                      : undefined
                                  }
                                >
                                  {g.fullQuality}
                                  {poolTarget > 0 ? ` / ${poolTarget}` : ''} variants
                                  {g.live > 0 ? ` + ${g.live} live` : ''}
                                </td>
                                <td>{g.plays}</td>
                                <td />
                                <td />
                              </tr>
                              {g.entries.map(e => (
                                <tr key={e.id}>
                                  {/* The short id is what GET /admin/gesture-pools/{id}
                                      takes, so a variant that looks wrong here can be
                                      pulled up with its raw tracks; the prompt actually
                                      sent to kimodo rides the tooltip. */}
                                  <td className={styles.poolVariantCell} title={e.prompt}>
                                    ↳ {e.id.slice(0, 8)}
                                  </td>
                                  <td className={styles.synonymCell}>{e.motionClass}</td>
                                  <td className={styles.synonymCell}>
                                    {e.source}
                                  </td>
                                  <td className={styles.synonymCell}>{e.durationSec != null ? `${e.durationSec.toFixed(1)}s` : '—'}</td>
                                  <td className={styles.synonymCell}>{e.playCount}</td>
                                  <td className={styles.synonymCell}>{e.createdAt.slice(0, 10)}</td>
                                  <td>
                                    <button
                                      className={styles.chipRemove}
                                      onClick={() => deleteEntry(e.id)}
                                      disabled={deletingId !== null}
                                      title="Delete this variant"
                                    >×</button>
                                  </td>
                                </tr>
                              ))}
                            </Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {entries && entries.length > 0 && (
                    <button
                      className={styles.deleteBtn}
                      onClick={clearPool}
                      disabled={clearing || entries.length === 0}
                    >
                      {clearing ? 'Clearing…' : `Clear pool (${entries.length} entries)`}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* ── Gestures ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Gestures</p>
              {animLoading && <span className={styles.defaultNote}>Loading…</span>}
              {animError && <p className={styles.saveError}>{animError}</p>}
              {animConfig && (
                <>
                  {animConfig.gestures.length === 0 ? (
                    <span className={styles.defaultNote}>(no gestures — using global defaults)</span>
                  ) : (
                    <div className={styles.chipList}>
                      {animConfig.gestures.map((g: GestureDTO) => (
                        <span key={g.id} className={styles.chip}>
                          {g.name}
                          <button
                            className={styles.chipRemove}
                            onClick={() => removeGesture(g.name)}
                            disabled={gesturesSaving}
                            title="Remove"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={styles.addRow}>
                    <input
                      className={styles.addInput}
                      placeholder="New gesture name…"
                      value={newGesture}
                      onChange={e => setNewGesture(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addGesture()}
                      disabled={gesturesSaving}
                    />
                    <button
                      className={styles.addBtn}
                      onClick={addGesture}
                      disabled={gesturesSaving || !newGesture.trim()}
                    >Add</button>
                  </div>

                  {/* Synonyms sub-section */}
                  <button className={styles.synonymsToggle} onClick={() => setShowSynonyms(s => !s)}>
                    {showSynonyms ? 'Hide synonyms' : `Show synonyms (${animConfig.gestureSynonyms.length})`}
                  </button>
                  {showSynonyms && (
                    <table className={styles.synonymTable}>
                      <thead>
                        <tr><th>Stems (comma-separated)</th><th>Maps to gesture</th><th /></tr>
                      </thead>
                      <tbody>
                        {animConfig.gestureSynonyms.map((s, i) => (
                          <tr key={i}>
                            <td className={styles.synonymCell}>{s.stemWords}</td>
                            <td className={styles.synonymCell}>{s.gestureName}</td>
                            <td>
                              <button
                                className={styles.chipRemove}
                                onClick={() => removeSynonym(i)}
                                disabled={synonymsSaving}
                                title="Remove"
                              >×</button>
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td>
                            <input
                              className={styles.addInput}
                              placeholder="e.g. grin, smirk"
                              value={newSynonymStems}
                              onChange={e => setNewSynonymStems(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addSynonym()}
                              disabled={synonymsSaving}
                            />
                          </td>
                          <td>
                            <input
                              className={styles.addInput}
                              placeholder="e.g. smile"
                              value={newSynonymGesture}
                              onChange={e => setNewSynonymGesture(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addSynonym()}
                              disabled={synonymsSaving}
                            />
                          </td>
                          <td>
                            <button
                              className={styles.addBtn}
                              onClick={addSynonym}
                              disabled={synonymsSaving || !newSynonymStems.trim() || !newSynonymGesture.trim()}
                            >{synonymsSaving ? 'Saving…' : 'Add'}</button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>

            {/* ── Vocal Cues ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Vocal Cues</p>
              {animConfig && (
                <>
                  {animConfig.vocalCues.length === 0 ? (
                    <span className={styles.defaultNote}>(no vocal cues — using global defaults)</span>
                  ) : (
                    <div className={styles.chipList}>
                      {animConfig.vocalCues.map(name => (
                        <span key={name} className={styles.chip}>
                          {name}
                          <button
                            className={styles.chipRemove}
                            onClick={() => removeVocalCue(name)}
                            disabled={vocalCuesSaving}
                            title="Remove"
                          >×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className={styles.addRow}>
                    <input
                      className={styles.addInput}
                      placeholder="New vocal cue name…"
                      value={newVocalCue}
                      onChange={e => setNewVocalCue(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addVocalCue()}
                      disabled={vocalCuesSaving}
                    />
                    <button
                      className={styles.addBtn}
                      onClick={addVocalCue}
                      disabled={vocalCuesSaving || !newVocalCue.trim()}
                    >Add</button>
                  </div>
                </>
              )}
            </div>

            {/* ── A2F Configuration ── */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>A2F Configuration</p>
                <button className={styles.a2fToggle} onClick={() => setShowA2F(s => !s)}>
                  {showA2F ? 'collapse' : 'expand'}
                </button>
              </div>
              {showA2F && (
                <>
                  <textarea
                    className={styles.personalityTextarea}
                    aria-label="A2F configuration JSON"
                    value={a2fConfigText}
                    onChange={e => setA2fConfigText(e.target.value)}
                    rows={16}
                    disabled={a2fSaving}
                  />
                  {a2fError && <p className={styles.saveError}>{a2fError}</p>}
                  <div className={styles.a2fActions}>
                    <button className={styles.saveBtn} onClick={saveA2FConfig} disabled={a2fSaving}>
                      {a2fSaving ? 'Saving…' : 'Save A2F Config'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ── Linked Stories ── */}
            <div className={styles.section}>
              <p className={styles.sectionTitle}>Linked Stories ({character.stories.length})</p>
              {character.stories.length === 0 ? (
                <span className={styles.noStories}>No stories linked to this character.</span>
              ) : (
                <div className={styles.storyGrid}>
                  {character.stories.map(s => (
                    <div key={s.id} className={styles.storyCard}>
                      <div className={styles.cardHeader}>
                        <span className={styles.cardTitle}>{s.title || s.sourceFilename || s.id}</span>
                      </div>
                      <span className={styles.characterId}>{s.id}</span>
                      <div className={styles.storyCardFooter}>
                        {s.sourceFilename && (
                          <>
                            <a
                              className={styles.startChatBtn}
                              href={`?story=${encodeURIComponent(s.sourceFilename)}#chat`}
                            >
                              Start Chat
                            </a>
                            <a
                              className={styles.beginStoryBtn}
                              href={`?story=${encodeURIComponent(s.sourceFilename)}&features=storyteller#chat`}
                            >
                              Begin Story
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
